import React, { type FC, useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import { useLazyQuery } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useSnackbar } from "notistack";

import { confirm } from 'components/Shared/ConfirmDialog';
import { Button } from 'components/Shared/Inputs';
import { useTimezoneUtils } from "hooks/useTimezoneUtils";
import { useFeatureManager } from 'hooks/useFeatureManager';
import { KebabMenu, Spinner } from "components/Shared";
import { useAppConfig } from "providers";
import { EditLicenseModal } from "./EditLicenseModal";
import { LICENSE_REF_DATA, } from "./queries";
import { type License, type RenewLicenseHistoryState, type ViewMode } from '../../../interfaces';
import { useHistoryState } from "hooks/useHistoryState";
import { LabelValue } from "components/Shared/LabelValue";
import { ApiError } from "api/error/ApiError";
import { useDeleteCredential } from "api/queries/credentialsQuery";

interface LicenseAndRegistrationFormProps {
    providerId: number;
    licenses?: License[];
    license?: License;
    cachePendingUpdate: boolean;
    onLicensesUpdated: (licenses: License[]) => void;
}

const MAX_UPLOADS_ALLOWED = 10;

export const LicenseAndRegistrationsForm: FC<LicenseAndRegistrationFormProps> = (props) => {
    const { providerId, licenses, license, cachePendingUpdate, onLicensesUpdated } = props;
    const [historyState, setHistoryState] = useHistoryState<RenewLicenseHistoryState>();

    const { REACT_APP_FILEUPLOAD_HOST } = useAppConfig();
    
    const { isProviderProfileSelfServeFeatureEnabled } = useFeatureManager();

    const { t } = useTranslation(['providerprofile', 'common', 'fileupload'], { useSuspense: false });

    const { localeDateFormat } = useTimezoneUtils();

    const [requestPending, setRequestPending] = useState(false);

    const isBeingRenewed = historyState?.renewLicenseId && historyState.renewLicenseId === license?.providerLicenseId
    
    const [inEdit, setInEdit] = useState(isBeingRenewed ? true : false);

    const [viewMode, setViewMode] = useState<ViewMode>(isBeingRenewed ? 'renew' : 'new');
    const formRef = useRef<HTMLDivElement|null>(null);

    const [getRefData, refDataQuery] = useLazyQuery(LICENSE_REF_DATA);
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();
    const timeoutRef = useRef<number|null>(null);
    const clearTimeoutRef = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = null;
    };

    const refreshAttachments = () => {
        setRequestPending(true);
        // introduced a 2-second delay because the API doesn't return the latest upload if we run a search immediately after upload
        timeoutRef.current = window.setTimeout(
            () => {
                queryClient.invalidateQueries({ queryKey: ['files', 'search', { Id: providerId, Type: 'provider' }]});
                clearTimeoutRef();
            },
            2000
        );
    };

    useEffect(() => clearTimeoutRef, []);
    
    useEffect(() => {
        if (inEdit && !refDataQuery.called) {
            getRefData();
        }
    }, [inEdit]);

    useEffect(() => {
        if (!!license?.attachments?.length) {
            setRequestPending(false);
        }
    }, [license?.attachments?.length, license?.attachments?.[0]?.fileName]);

    useEffect(() => {
        if (isBeingRenewed) {
            formRef.current?.scrollIntoView({ behavior: 'smooth' });
            if (!inEdit) {
                setInEdit(true);
                setViewMode('renew');
            }
            setHistoryState({ renewLicenseId: null })
        }
    }, [isBeingRenewed]);

    const onError = (error: Error) => {
        if (error instanceof ApiError && error.isBusinessError) {
            enqueueSnackbar(t(error.code), { variant: 'error' });
        } else {
            enqueueSnackbar(error.message, { variant: 'error' });
        }
    }

    const { mutateAsync: deleteLicense } = useDeleteCredential({
        onError,
        onSuccess: () => console.info(`License ${license?.licenseTypeDescr ?? ""} ${license?.registrationNo} deleted successfully!`)
    });

    const handleDelete = async () => {
        const dialogProps = {
            title: t('common__delete_entry_dialog_title'),
            message: t('common__delete_entry_dialog_text'),
            confirmLabel: t('licenses_registrations__yes_delete'),
        }
        if (license?.providerLicenseId && await confirm(dialogProps)) {
            setRequestPending(true);
            deleteLicense(license.providerLicenseId.toString())
                .then(()=> onLicensesUpdated([
                    ...(licenses?.filter(l => l.providerLicenseId !== license.providerLicenseId) || [])
                ]))
                .then(refreshAttachments)
                .catch((err) => enqueueSnackbar(err.message, { variant: "error" }))
                .finally(() => setRequestPending(false));
        }
    }

    const handleAddEntry = () => {
        setViewMode('new');
        setInEdit(true);
    }
    
    const handleRenew = () => {
        setViewMode('renew');
        setInEdit(true);
    }

    const handleUploadOnly = () => {
        setViewMode('upload-only');
        setInEdit(true);
    }

    const handleEdit = () => {
        setViewMode('edit');
        setInEdit(true);
    }

    const handleDownload = (fileId: string) => {
        console.info(`🌎 opening ${REACT_APP_FILEUPLOAD_HOST}/v1/file/${fileId}/download`);
        window.open(`${REACT_APP_FILEUPLOAD_HOST}/v1/file/${fileId}/download`, '_blank', 'noreferrer');
    }

    return !license && !inEdit
        ? <div>
            {isProviderProfileSelfServeFeatureEnabled() && (
                <Button variant="text" onClick={handleAddEntry} className="add_entry_button">
                    <AddIcon/>{t('common__add_entry_button_label')}
                </Button>
            )}
        </div>
        : <Container ref={formRef} className="license_and_registration_form provider_profile_section_container_padding_top">
            {
                requestPending && <Spinner fillParent backdrop />
            }
            {
                license &&
                <Grid container>
                    <Grid item xs={12} sm={12}>
                        <Grid container className="license_info_section">
                            <Grid item xs={12} sm={7}>
                                <LabelValue label={t('licenses_registrations__association_label')} value={license.licenseRegistrationAssociation} />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <LabelValue label={t('licenses_registrations__expiry_date_label')} value={moment(license.expiryDate).format(localeDateFormat)} />
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <LabelValue label={t('licenses_registrations__registration_number_label')} value={license.registrationNo} />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <LabelValue label={t('licenses_registrations__province_label')} value={`${license.provinceString} ${license.countryCode ?? ''}`} />
                            </Grid>
                        </Grid>
                        <Grid container className="action_buttons_section">
                            <Grid item>
                                <KebabMenu
                                    getMenuItems={() => ([
                                        { label: t('common:renew'), onClick: handleRenew },
                                        { label: t('common:edit'), onClick: handleEdit },
                                        { label: t('licenses_registrations__add_document'), onClick: handleUploadOnly, hidden: (license?.attachments?.length ?? 0) >= MAX_UPLOADS_ALLOWED },
                                        { label: t('common:delete'), onClick: handleDelete, hidden: (licenses?.length ?? 0) <= 1 }
                                    ])}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    {license.attachments?.map(({ fileName, fileId, createdOn}) => (
                        <div className="attachments_wrapper" key={fileId}>
                            <div className="attachment_section">
                                <div className="file_icon">
                                    <UploadFileIcon className="file-icon" />
                                </div>
                            
                                <div className="attachment_present">
                                    <p className='global__paragraph'><a href={`${REACT_APP_FILEUPLOAD_HOST}/v1/file/${fileId}/preview`} target="_blank" rel="noreferrer">{fileName}</a></p>
                                    <p className='global__paragraph--small'>{t('licenses_registrations__uploaded')} {moment(createdOn).format('ddd ll')}</p>
                                </div>
                                <div className="download_section">
                                    <Button variant="text" onClick={() => handleDownload(fileId)}><DownloadIcon />&nbsp;{t('fileupload:download')}</Button>
                                </div>
                            </div>
                        </div>
                    ))}     
                    {!license.attachments?.length && (
                        <div className="attachments_wrapper">
                            <div className="attachment_section">
                                <div className="file_icon">
                                    <UploadFileIcon className="file-icon" />
                                </div>
                                <div className="attachment_absent">
                                    <p className='global__paragraph'>{t('licenses_registrations__no_docs_found')}</p>
                                    <p className='action_button_segment'><Button onClick={handleUploadOnly}>{t('licenses_registrations__upload_doc')}</Button></p>
                                </div>
                        
                            </div>
                        </div>
                    )}
                    {(license?.attachments?.length ?? 0) >= MAX_UPLOADS_ALLOWED && (
                        <Alert className="upload_limit_warning" variant="standard" color="info" icon={<InfoIcon color="primary" />}>
                            {t('licenses_registrations__max_upload_reached')}
                        </Alert>
                    )}
                </Grid>
            }
            <EditLicenseModal
                viewMode={viewMode}
                open={inEdit}
                cachePendingUpdate={cachePendingUpdate}
                license={license}
                licenses={licenses}
                setModalOpen={setInEdit}
                providerId={providerId}
                refreshAttachments={refreshAttachments}
                onLicensesUpdated={onLicensesUpdated}
            />
        </Container>
}